import type { Player } from "../engine/player";
import { Strings } from "../engine/strings";
import type { Event } from "../engine/events/event";
import { IconLoader } from "./dom";
import { playUiSound, UiSound } from "./sound/uiSoundPlayer";
import { HudViewModel, HUD_WEAPON_SLOT_COUNT, type WeaponSlot, type EventLogEntry } from "./viewModels/hudViewModel";

const CARD_WIDTH = 110;
const CARD_HEIGHT = 60;
const ICON_SIZE = 28;
const DEPTH_OFFSET_X = 4;
const DEPTH_OFFSET_Y = 3;
const DEPTH_ROTATIONS = [3, -2];

// Every slot reserves the same footprint (card, stack, or placeholder) so the leftmost x stays stable across pickups.
const SLOT_PAD = 8;
const SLOT_WIDTH = CARD_WIDTH + SLOT_PAD; // 118
const SLOT_HEIGHT = CARD_HEIGHT + SLOT_PAD; // 68
const SLOT_GAP = 14;
const WEAPON_BAR_PADDING_X = 8;
const WEAPON_BAR_PADDING_TOP = 6;
const WEAPON_BAR_PADDING_BOTTOM = 14;
export const WEAPON_BAR_HEIGHT = SLOT_HEIGHT + WEAPON_BAR_PADDING_TOP + WEAPON_BAR_PADDING_BOTTOM; // 88
export const WEAPON_BAR_WIDTH =
	HUD_WEAPON_SLOT_COUNT * SLOT_WIDTH + (HUD_WEAPON_SLOT_COUNT - 1) * SLOT_GAP + WEAPON_BAR_PADDING_X * 2;
// = 7*118 + 6*14 + 16 = 926

// Health bar spans 3 slot-widths — visually matches the leftmost 3 weapon slots.
const HEALTH_BAR_SLOTS = 3;
const HEALTH_BAR_WIDTH = HEALTH_BAR_SLOTS * SLOT_WIDTH + (HEALTH_BAR_SLOTS - 1) * SLOT_GAP + WEAPON_BAR_PADDING_X * 2;
// = 3*118 + 2*14 + 16 = 398
const HEALTH_BAR_PADDING_Y = 5;
const HEALTH_BAR_INNER_HEIGHT = 16;
export const HEALTH_BAR_HEIGHT = HEALTH_BAR_INNER_HEIGHT + HEALTH_BAR_PADDING_Y * 2; // 32

/** Maps eventBinding's hex colour tokens to .evt-* CSS classes — kept here so eventBinding stays DOM-agnostic. */
const EVT_COLOR_CLASS: Record<string, string> = {
	"#ffffff": "evt-white",
	"#ff0000": "evt-red",
	"#00ff00": "evt-green",
	"#00bfff": "evt-blue",
	"#ffff00": "evt-yellow",
	"#ffcc00": "evt-gold",
	"#ff4500": "evt-orange",
};

export class HudRenderer {
	private player: Player;
	private viewModel: HudViewModel;
	private healthLabel: HTMLDivElement;
	private hpBarOuter: HTMLDivElement;
	private hpFill: HTMLDivElement;
	private hpText: HTMLDivElement;
	private weaponBar: HTMLElement;
	private eventLogOuter: HTMLDivElement;
	private eventLogInner: HTMLDivElement;

	/** Triggers the card-rotate-in animation only when the selected weapon actually changes. */
	private previousSelectedWeapon: Map<number, unknown> = new Map();

	constructor(
		container: HTMLElement,
		weaponBar: HTMLElement,
		healthBar: HTMLElement,
		player: Player,
		viewModel: HudViewModel = new HudViewModel(),
	) {
		this.player = player;
		this.viewModel = viewModel;
		this.weaponBar = weaponBar;

		this.weaponBar.classList.add("weapon-bar");
		this.weaponBar.style.width = `${WEAPON_BAR_WIDTH}px`;
		this.weaponBar.style.height = `${WEAPON_BAR_HEIGHT}px`;
		this.weaponBar.style.gap = `${SLOT_GAP}px`;
		this.weaponBar.style.padding = `${WEAPON_BAR_PADDING_TOP}px ${WEAPON_BAR_PADDING_X}px ${WEAPON_BAR_PADDING_BOTTOM}px`;

		healthBar.classList.add("health-bar-row");
		healthBar.style.width = `${HEALTH_BAR_WIDTH}px`;
		healthBar.style.height = `${HEALTH_BAR_HEIGHT}px`;
		healthBar.style.padding = `${HEALTH_BAR_PADDING_Y}px ${WEAPON_BAR_PADDING_X}px`;

		this.healthLabel = document.createElement("div");
		this.healthLabel.className = "health-bar-label";
		this.healthLabel.textContent = Strings.get("ui_labelHealth") ?? "Health:";
		healthBar.appendChild(this.healthLabel);

		this.hpBarOuter = document.createElement("div");
		this.hpBarOuter.className = "health-bar";
		this.hpBarOuter.style.height = `${HEALTH_BAR_INNER_HEIGHT}px`;
		healthBar.appendChild(this.hpBarOuter);

		this.hpFill = document.createElement("div");
		this.hpFill.className = "health-bar-fill";
		this.hpBarOuter.appendChild(this.hpFill);

		this.hpText = document.createElement("div");
		this.hpText.className = "health-bar-text";
		this.hpBarOuter.appendChild(this.hpText);

		this.eventLogOuter = document.createElement("div");
		this.eventLogOuter.className = "event-log";
		container.appendChild(this.eventLogOuter);

		this.eventLogInner = document.createElement("div");
		this.eventLogInner.className = "event-log-inner";
		this.eventLogOuter.appendChild(this.eventLogInner);
	}

	update(): void {
		const health = this.viewModel.computeHealthBar(this.player);
		this.hpFill.style.width = `${health.fillPercent}%`;
		this.hpText.textContent = health.label;
		this.hpBarOuter.classList.toggle("low-health", health.isLow);

		this.weaponBar.innerHTML = "";
		const slots = this.viewModel.computeWeaponSlots(this.player.arsenal);
		for (const slot of slots) {
			if (slot.weapon === null) {
				this.weaponBar.appendChild(this.buildPlaceholderSlot(slot.familyId));
			} else {
				this.weaponBar.appendChild(this.buildSlotForFamily(slot));
			}
		}
	}

	private buildSlotForFamily(slot: WeaponSlot): HTMLDivElement {
		// Animate only when the weapon reference actually changes — a no-op update shouldn't re-trigger.
		const previousWeapon = this.previousSelectedWeapon.get(slot.familyId);
		const shouldAnimate = previousWeapon !== undefined && previousWeapon !== slot.weapon;
		this.previousSelectedWeapon.set(slot.familyId, slot.weapon);

		return this.buildFamilyStack(slot, shouldAnimate);
	}

	private buildFamilyStack(slot: WeaponSlot, shouldAnimate: boolean): HTMLDivElement {
		const depthCount = Math.min(slot.familySize - 1, 2);

		const stack = document.createElement("div");
		stack.className = "weapon-stack";
		stack.style.width = `${SLOT_WIDTH}px`;
		stack.style.height = `${SLOT_HEIGHT}px`;

		for (let i = depthCount; i >= 1; i--) {
			stack.appendChild(this.buildDepthCard(i));
		}

		stack.appendChild(this.buildMainCard(slot, shouldAnimate));
		return stack;
	}

	private buildPlaceholderSlot(familyId: number): HTMLDivElement {
		const slot = document.createElement("div");
		slot.className = "weapon-stack";
		slot.style.width = `${SLOT_WIDTH}px`;
		slot.style.height = `${SLOT_HEIGHT}px`;

		const card = document.createElement("div");
		card.className = "weapon-slot empty";
		card.style.width = `${CARD_WIDTH}px`;
		card.style.height = `${CARD_HEIGHT}px`;
		slot.appendChild(card);

		const hotkey = document.createElement("div");
		hotkey.className = "weapon-hotkey placeholder";
		hotkey.textContent = String(familyId);
		card.appendChild(hotkey);

		return slot;
	}

	private buildDepthCard(layer: number): HTMLDivElement {
		const el = document.createElement("div");
		el.className = "weapon-depth-card";
		const rot = DEPTH_ROTATIONS[layer - 1] ?? 0;
		el.style.left = `${layer * DEPTH_OFFSET_X}px`;
		el.style.bottom = `${layer * DEPTH_OFFSET_Y}px`;
		el.style.width = `${CARD_WIDTH}px`;
		el.style.height = `${CARD_HEIGHT}px`;
		el.style.transform = `rotate(${rot}deg)`;
		return el;
	}

	private buildMainCard(slot: WeaponSlot, shouldAnimate: boolean): HTMLDivElement {
		const card = document.createElement("div");
		card.className = slot.isActive ? "weapon-slot active" : "weapon-slot";
		card.style.width = `${CARD_WIDTH}px`;
		card.style.height = `${CARD_HEIGHT}px`;

		const familyId = slot.familyId;
		card.addEventListener("click", () => {
			playUiSound(UiSound.WEAPON_SWITCH);
			this.player.selectWeaponFamily(familyId);
			this.update();
		});

		if (shouldAnimate) {
			card.classList.add("anim-card-rotate-in");
		}

		const hotkey = document.createElement("div");
		hotkey.className = slot.isActive ? "weapon-hotkey" : "weapon-hotkey inactive";
		hotkey.textContent = familyId === 0 ? "0" : String(familyId);
		card.appendChild(hotkey);

		const iconContainer = this.buildIconContainer(slot);
		card.appendChild(iconContainer);

		const nameEl = document.createElement("div");
		nameEl.className = slot.isActive ? "weapon-name" : "weapon-name inactive";
		nameEl.textContent = slot.displayName;
		nameEl.style.maxWidth = `${CARD_WIDTH - 8}px`;
		card.appendChild(nameEl);

		const ammoEl = document.createElement("div");
		ammoEl.className = slot.isActive ? "weapon-ammo" : "weapon-ammo inactive";
		if (slot.cooldownTurns > 0) {
			ammoEl.textContent = Strings.get("ui_recharging", slot.cooldownTurns) ?? `Recharging (${slot.cooldownTurns})`;
		} else {
			ammoEl.textContent = slot.isInfiniteAmmo ? "\u221E" : String(slot.ammo);
		}
		card.appendChild(ammoEl);

		return card;
	}

	private buildIconContainer(slot: WeaponSlot): HTMLDivElement {
		const container = document.createElement("div");
		container.className = slot.isActive ? "weapon-icon" : "weapon-icon inactive";
		container.style.width = `${ICON_SIZE}px`;
		container.style.height = `${ICON_SIZE}px`;

		const filename = slot.iconFilename;
		if (!filename) return container;

		const cached = IconLoader.getCachedIcon(filename, ICON_SIZE);
		if (cached) {
			container.appendChild(IconLoader.copyIconCanvas(cached));
		} else {
			IconLoader.loadIcon(filename, ICON_SIZE).then((img) => {
				// Patch directly so in-progress card animations aren't killed by a full rebuild.
				if (container.isConnected) {
					container.appendChild(IconLoader.copyIconCanvas(img));
				}
			});
		}

		return container;
	}

	processEvents(events: Event[]): void {
		const entries = this.viewModel.computeEventLogEntries(events);
		for (const entry of entries) {
			this.eventLogInner.appendChild(this.buildEventLine(entry));
		}
		this.eventLogOuter.scrollTop = this.eventLogOuter.scrollHeight;
	}

	private buildEventLine(entry: EventLogEntry): HTMLDivElement {
		const line = document.createElement("div");
		line.className = "event-log-line";
		for (const seg of entry.lines) {
			const span = document.createElement("span");
			const classes: string[] = [];
			const colorClass = EVT_COLOR_CLASS[seg.colorKey];
			if (colorClass) classes.push(colorClass);
			if (seg.bold) classes.push("evt-bold");
			if (classes.length > 0) span.className = classes.join(" ");
			span.textContent = seg.text;
			line.appendChild(span);
		}
		return line;
	}

	refreshLabels(): void {
		this.healthLabel.textContent = Strings.get("ui_labelHealth") ?? "Health:";
		this.update();
	}
}
